import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Quote, PenSquare, X, Send, Check } from 'lucide-react';
import { testimonialsAPI } from '../services/api';

// Seed reviews (used when no admin-approved reviews are available yet).
// Aligned with cars marked "sold" in the inventory.
const SEED_TESTIMONIALS = [
  { id: 'seed-1', name: 'Nicolas D.', city: 'Wavre',     vehicle: 'BMW Série 1 118i',              rating: 5, tKey: 'testimonials.t1' },
  { id: 'seed-2', name: 'Sophie L.',  city: 'Bruxelles', vehicle: 'Mercedes-Benz Classe E 220',    rating: 5, tKey: 'testimonials.t2' },
  { id: 'seed-3', name: 'Julien M.',  city: 'Namur',     vehicle: 'Volkswagen Golf VII 1.4 TSI',   rating: 4, tKey: 'testimonials.t3' },
];

const BADGE_COLORS = [
  'bg-blue-900 text-white',
  'bg-emerald-600 text-white',
  'bg-amber-500 text-white',
  'bg-rose-500 text-white',
  'bg-indigo-600 text-white',
  'bg-teal-600 text-white',
];

const initials = (name = '') =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const StarRow = ({ value, size = 14, interactive = false, onChange }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value}/5`}>
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= value;
      const cls = filled ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200';
      if (interactive) {
        return (
          <button
            type="button"
            key={n}
            onClick={() => onChange && onChange(n)}
            className={`transition-transform hover:scale-110 ${filled ? '' : 'hover:text-amber-300'}`}
            aria-label={`${n} ${n === 1 ? 'étoile' : 'étoiles'}`}
            data-testid={`review-star-${n}`}
          >
            <Star size={size + 6} className={cls} />
          </button>
        );
      }
      return <Star key={n} size={size} className={cls} />;
    })}
  </div>
);

const TestimonialCard = ({ r, idx, quote }) => (
  <article
    className="relative bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow"
    data-testid={`testimonial-card-${r.id}`}
  >
    <Quote className="absolute top-5 right-5 text-slate-100" size={40} aria-hidden="true" />
    <StarRow value={r.rating} />
    <p className="mt-4 text-slate-700 text-sm leading-relaxed relative">“{quote}”</p>
    <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-3">
      <div
        className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm ${BADGE_COLORS[idx % BADGE_COLORS.length]}`}
        aria-hidden="true"
      >
        {initials(r.name)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-slate-900 text-sm font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {r.name}
        </p>
        <p className="text-xs text-slate-500 mt-0.5 truncate">
          {[r.city, r.vehicle].filter(Boolean).join(' · ') || '—'}
        </p>
      </div>
    </div>
  </article>
);

/* ==================== SUBMIT REVIEW MODAL ==================== */
const ReviewModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', city: '', vehicle: '', email: '', comment: '' });
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: '', city: '', vehicle: '', email: '', comment: '' });
      setRating(0);
      setErrorMsg('');
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.name.trim()) {
      setErrorMsg(t('reviewForm.errName', 'Veuillez indiquer votre nom.'));
      return;
    }
    if (rating < 1) {
      setErrorMsg(t('reviewForm.errRating', 'Veuillez sélectionner une note.'));
      return;
    }
    if (form.comment.trim().length < 20) {
      setErrorMsg(t('reviewForm.errComment', 'Votre commentaire doit contenir au moins 20 caractères.'));
      return;
    }
    setSubmitting(true);
    try {
      await testimonialsAPI.submit({
        name: form.name.trim(),
        city: form.city.trim() || null,
        vehicle: form.vehicle.trim() || null,
        email: form.email.trim() || null,
        rating,
        comment: form.comment.trim(),
      });
      setSuccess(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(detail || t('reviewForm.errGeneric', "Une erreur est survenue. Merci de réessayer."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="review-modal"
    >
      <div
        className="bg-white w-full max-w-lg rounded-3xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">
              {t('reviewForm.eyebrow', 'Votre avis')}
            </span>
            <h3
              className="text-xl font-bold text-slate-900 leading-tight mt-1"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t('reviewForm.title', 'Laisser un avis')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-900 rounded-full hover:bg-slate-100 transition-colors"
            aria-label="Close"
            data-testid="review-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center" data-testid="review-modal-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <Check size={26} />
            </div>
            <h4
              className="text-lg font-bold text-slate-900 mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t('reviewForm.successTitle', 'Merci pour votre avis !')}
            </h4>
            <p className="text-sm text-slate-600 mb-6">
              {t(
                'reviewForm.successBody',
                'Votre témoignage a été envoyé. Il sera publié après validation par notre équipe.'
              )}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="btn-primary w-full"
              data-testid="review-modal-success-close"
            >
              {t('reviewForm.close', 'Fermer')}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="form-label">
                {t('reviewForm.rating', 'Votre note')} <span className="text-rose-500">*</span>
              </label>
              <div className="mt-1">
                <StarRow value={rating} interactive size={14} onChange={setRating} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="form-label">
                  {t('reviewForm.name', 'Nom')} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={setField('name')}
                  className="form-input"
                  placeholder={t('reviewForm.namePh', 'Marie D.')}
                  required
                  data-testid="review-input-name"
                />
              </div>
              <div>
                <label className="form-label">{t('reviewForm.city', 'Ville')}</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={setField('city')}
                  className="form-input"
                  placeholder={t('reviewForm.cityPh', 'Wavre')}
                  data-testid="review-input-city"
                />
              </div>
            </div>

            <div>
              <label className="form-label">{t('reviewForm.vehicle', 'Véhicule acheté (optionnel)')}</label>
              <input
                type="text"
                value={form.vehicle}
                onChange={setField('vehicle')}
                className="form-input"
                placeholder={t('reviewForm.vehiclePh', 'BMW Série 1 118i')}
                data-testid="review-input-vehicle"
              />
            </div>

            <div>
              <label className="form-label">
                {t('reviewForm.comment', 'Votre commentaire')} <span className="text-rose-500">*</span>
              </label>
              <textarea
                rows={4}
                value={form.comment}
                onChange={setField('comment')}
                className="form-input resize-none"
                placeholder={t('reviewForm.commentPh', 'Racontez-nous votre expérience (min. 20 caractères)…')}
                required
                minLength={20}
                data-testid="review-input-comment"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                {form.comment.length} / 500
              </p>
            </div>

            <div>
              <label className="form-label">{t('reviewForm.email', 'Email (privé, non publié)')}</label>
              <input
                type="email"
                value={form.email}
                onChange={setField('email')}
                className="form-input"
                placeholder="jean.dupont@email.be"
                data-testid="review-input-email"
              />
            </div>

            {errorMsg && (
              <div
                className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2"
                data-testid="review-error"
              >
                {errorMsg}
              </div>
            )}

            <p className="text-[11px] text-slate-500 leading-relaxed">
              {t(
                'reviewForm.moderationNote',
                'Votre avis sera vérifié par notre équipe avant publication sur le site.'
              )}
            </p>

            <button
              type="submit"
              disabled={submitting}
              className="w-full btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="review-submit"
            >
              <Send size={16} />
              {submitting ? t('reviewForm.sending', 'Envoi…') : t('reviewForm.submit', 'Envoyer mon avis')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ==================== TESTIMONIALS SECTION ==================== */
const Testimonials = () => {
  const { t } = useTranslation();
  const [approved, setApproved] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    testimonialsAPI
      .getApproved()
      .then((res) => {
        if (mounted) setApproved(res.data || []);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [modalOpen]); // refetch after modal closes (in case user just submitted)

  // Merge: prefer real approved reviews first, then fill with seed to always show at least 3.
  const list = React.useMemo(() => {
    const realMapped = approved.map((a) => ({
      id: a.id,
      name: a.name,
      city: a.city,
      vehicle: a.vehicle,
      rating: a.rating,
      comment: a.comment,
    }));
    // Pad with seed until we reach 3, but never duplicate seed if we have real ones.
    if (realMapped.length >= 3) return realMapped.slice(0, 6);
    const need = 3 - realMapped.length;
    return [...realMapped, ...SEED_TESTIMONIALS.slice(0, need)];
  }, [approved]);

  const avgRating = list.length
    ? (list.reduce((s, x) => s + (x.rating || 0), 0) / list.length).toFixed(1)
    : '5,0';

  return (
    <section
      className="py-16 sm:py-20 bg-[#F7F8FA] border-y border-slate-200"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="section-eyebrow">{t('testimonials.eyebrow', 'Avis clients')}</span>
            <h2
              className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t('testimonials.title', 'Ils nous ont fait confiance')}
            </h2>
            <p className="mt-4 text-slate-600">
              {t(
                'testimonials.subtitle',
                'Des acheteurs conquis par notre sélection contrôlée et notre accompagnement de A à Z.'
              )}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 self-start md:self-end">
            <div
              className="inline-flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm"
              data-testid="testimonials-avg-rating"
            >
              <div
                className="text-3xl font-extrabold text-slate-900 leading-none"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {String(avgRating).replace('.', ',')}
              </div>
              <div>
                <StarRow value={Math.round(Number(avgRating))} />
                <p className="text-xs text-slate-500 mt-1">
                  {t('testimonials.basedOn', 'basé sur {{count}} avis clients', {
                    count: list.length,
                  })}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 bg-blue-900 hover:bg-blue-800 text-white text-sm font-bold rounded-full px-5 py-3 transition-colors"
              data-testid="testimonials-leave-review"
            >
              <PenSquare size={14} />
              {t('testimonials.leaveReview', 'Laisser un avis')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {list.map((r, i) => (
            <TestimonialCard
              key={r.id}
              r={r}
              idx={i}
              quote={r.comment ? r.comment : t(r.tKey)}
            />
          ))}
        </div>
      </div>

      <ReviewModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
};

export default Testimonials;
